<script setup lang="ts">
import { DynamicIcon } from '@/components/primitives'
import { useComputedProps } from '../menu-items-container/use-computed-props'
import type { INavigationItem } from '@/models'
import type { TMenuItemContainerProps } from './models'

const props = withDefaults(defineProps<TMenuItemContainerProps>(), {
  isCollapsed: false,
})

const emits = defineEmits<{
  (e: 'menuItemClicked', params: INavigationItem): any
  (e: 'expandOrCollapse', params: INavigationItem): any
}>()

const {
  hasChildren,
  iconCss,
  badgeValueCss,
  menuItemCssClass,
  cssClassExpandedIndicator,
  childButtonCssClass,
} = useComputedProps(props)

const onClick = () => {
  emits('menuItemClicked', props.model)
}

const onChildClick = (e: MouseEvent, childItem: INavigationItem) => {
  e.stopImmediatePropagation()
  e.stopPropagation()
  e.preventDefault()
  emits('menuItemClicked', childItem)
}

const onExpandClick = () => {
  emits('expandOrCollapse', props.model)
  // nextTick(() => {
  //   if (internalState.isExpanded && model.children?.length) {
  //     onChildClick(model.children[0])
  //   }
  // })
}

// class="group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium"
</script>

<template>
  <div
    v-if="!hasChildren"
    :class="menuItemCssClass"
    :aria-current="model.selected ? 'page' : undefined"
    @click.stop="onClick"
  >
    <div :class="`flex items-center ${!isCollapsed ? 'gap-3' : ''}`">
      <DynamicIcon v-if="model.icon" :name="model.icon" :cssClass="iconCss" aria-hidden="true" />
      <span :class="isCollapsed ? 'sr-only' : 'flex-1'">
        {{ model.name }}
      </span>
    </div>
    <span v-if="!isCollapsed && model.badgeValue" :class="badgeValueCss">{{ model.badgeValue }}</span>
  </div>
  <div v-if="hasChildren" class="flex flex-col w-full">
    <button
      type="button"
      :class="menuItemCssClass"
      aria-controls="sub-menu-1"
      aria-expanded="false"
      @click.prevent="onExpandClick"
    >
      <div :class="`flex items-center ${!isCollapsed ? 'gap-3' : ''}`">
        <DynamicIcon v-if="model.icon" :name="model.icon" :cssClass="iconCss" aria-hidden="true" />
        <span :class="isCollapsed ? 'sr-only' : 'flex-1'">
          {{ model.name }}
        </span>
        <!-- badge -->
        <span v-if="!isCollapsed && !model.isExpanded && model.badgeValue" :class="badgeValueCss">{{
          model.badgeValue
        }}</span>
      </div>
      <!-- Expanded indicator -->
      <svg v-if="!isCollapsed" :class="cssClassExpandedIndicator" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
      </svg>
    </button>
    <!-- Expandable section -->
    <div
      v-show="model.isExpanded"
      id="sub-menu-1"
      :class="
        !model.isExpanded
          ? 'hidden'
          : isCollapsed
            ? 'ring-1 ring-primary ring-opacity-70 rounded-md'
            : 'flex flex-col gap-1'
      "
    >
      <a
        v-for="childItem in model.children"
        :key="childItem.id"
        :class="
          isCollapsed ? 'flex flex-col cursor-pointer ml-0 p-1' : 'flex flex-col cursor-pointer ml-7 p-0'
        "
        aria-controls="sub-menu-1"
        aria-expanded="false"
        :aria-current="childItem.selected ? 'page' : undefined"
        @click="(ev) => onChildClick(ev, childItem)"
      >
        <div :class="childButtonCssClass({ selected: childItem.selected } as any)">
          <DynamicIcon
            v-if="childItem.icon"
            :name="childItem.icon"
            :cssClass="`shrink-0 ${isCollapsed ? 'size-3' : 'size-4'}`"
            aria-hidden="true"
          />
          <span :class="isCollapsed ? 'sr-only' : 'flex-1'">
            {{ childItem.name }}
          </span>
        </div>
      </a>
    </div>
  </div>
</template>
